import { motion } from "framer-motion";
import { Phone } from "lucide-react";
import Manometer from "./Manometer";
import { WaButton } from "./WaButton";
import { PHONE_DISPLAY, PHONE_TEL } from "@/lib/constants";

const H1_LINES = ["Soluções hidráulicas", "e elétricas", "para sua instalação"];

export default function Hero() {
  return (
    <section id="inicio" className="relative overflow-hidden bg-blueprint pt-20 lg:pt-20">
      <div
        className="pointer-events-none absolute -right-40 top-1/2 h-[560px] w-[560px] -translate-y-1/2 rounded-full opacity-40"
        style={{ background: "radial-gradient(circle, rgba(0,163,224,0.25) 0%, rgba(10,92,140,0.12) 45%, transparent 70%)" }}
      />
      <div className="relative mx-auto grid max-w-7xl grid-cols-1 items-center gap-8 px-4 sm:px-6 lg:grid-cols-12 lg:gap-8 lg:px-8 lg:pb-20 lg:pt-14">
        <div className="lg:col-span-6">
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.05 }}
            className="flex items-center gap-3 font-mono text-xs uppercase tracking-[0.25em] text-[#0A5C8C]"
          >
            <span className="relative flex h-2.5 w-2.5">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-wa opacity-60" />
              <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-wa" />
            </span>
            Hidráulica · Elétrica · São Paulo e região
          </motion.p>

          <h1 data-testid="hidropro-hero-h1" className="mt-5 font-display text-4xl font-extrabold leading-[1.08] tracking-tight text-[#1A1A1A] sm:text-6xl lg:text-[3.6rem] xl:text-[4.1rem]">
            {H1_LINES.map((line, i) => (
              <span key={line} className="block overflow-hidden pb-[0.12em] -mb-[0.12em]">
                <motion.span
                  className={`block ${i === 2 ? "text-[#0A5C8C]" : ""}`}
                  initial={{ y: "112%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 0.9, delay: 0.2 + i * 0.13, ease: [0.22, 1, 0.36, 1] }}
                >
                  {line}
                  {i === 2 && (
                    <svg viewBox="0 0 260 12" className="mt-1 h-2.5 w-52 sm:w-64" preserveAspectRatio="none" aria-hidden="true">
                      <motion.path
                        d="M2 7 Q 34 1 66 7 T 130 7 T 194 7 T 258 7"
                        fill="none"
                        stroke="#00A3E0"
                        strokeWidth="3.5"
                        strokeLinecap="round"
                        initial={{ pathLength: 0 }}
                        animate={{ pathLength: 1 }}
                        transition={{ duration: 0.8, delay: 1.0, ease: "easeOut" }}
                      />
                    </svg>
                  )}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.75 }}
            className="mt-5 max-w-xl text-base leading-relaxed text-slate-600 sm:text-lg"
          >
            Serviços de instalação, manutenção, reparo e adequação de sistemas hidráulicos, motobombas, aquecimento de
            água e instalações elétricas em São Paulo e região.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.9 }}
            className="mt-7 flex flex-col items-start gap-4 sm:flex-row sm:items-center sm:gap-6"
          >
            <WaButton big testId="hidropro-hero-whatsapp-button" className="w-full sm:w-auto" />
            <a
              href={PHONE_TEL}
              data-testid="hidropro-hero-phone-link"
              className="inline-flex items-center gap-2 font-display text-lg font-bold text-[#1A1A1A] transition-colors hover:text-[#0A5C8C]"
            >
              <Phone className="h-4 w-4 text-[#0A5C8C]" />
              {PHONE_DISPLAY}
            </a>
          </motion.div>

          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.7, delay: 1.05 }}
            className="mt-5 max-w-md text-sm leading-relaxed text-slate-500"
          >
            Atendimento para residências, condomínios e empresas. Consulte a disponibilidade para sua região.
          </motion.p>
        </div>

        <motion.div
          className="lg:col-span-6"
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1, delay: 0.35, ease: [0.22, 1, 0.36, 1] }}
        >
          <div className="tick-corner relative rounded-2xl border border-[rgba(10,92,140,0.25)] bg-white/55 shadow-[0_30px_80px_rgba(10,92,140,0.12)] backdrop-blur-sm">
            <Manometer />
          </div>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 0.8 }}
        className="pointer-events-none absolute bottom-5 left-1/2 hidden -translate-x-1/2 flex-col items-center gap-1.5 lg:flex"
      >
        <svg viewBox="0 0 100 100" className="h-14 w-14" aria-hidden="true">
          <circle cx="50" cy="52" r="41" fill="#FFFFFF" stroke="rgba(10,92,140,0.45)" strokeWidth="4" />
          {Array.from({ length: 7 }, (_, i) => {
            const a = ((-110 + (i * 220) / 6) * Math.PI) / 180;
            return (
              <line
                key={i}
                x1={50 + Math.sin(a) * 34}
                y1={52 - Math.cos(a) * 34}
                x2={50 + Math.sin(a) * 40}
                y2={52 - Math.cos(a) * 40}
                stroke="rgba(10,92,140,0.5)"
                strokeWidth="2.5"
                strokeLinecap="round"
              />
            );
          })}
          <motion.g
            style={{ transformOrigin: "50px 52px" }}
            animate={{ rotate: [-50, -15, -50, 25, -50] }}
            transition={{ duration: 3.4, repeat: Infinity, ease: "easeInOut" }}
          >
            <line x1="50" y1="52" x2="50" y2="26" stroke="#D97706" strokeWidth="5" strokeLinecap="round" />
          </motion.g>
          <circle cx="50" cy="52" r="6" fill="#0A5C8C" />
        </svg>
        <span className="font-mono text-[9px] uppercase tracking-[0.3em] text-[#0A5C8C]/70">Role</span>
      </motion.div>
    </section>
  );
}
